/**
 * PR31 (portrait-only mobile gameplay). Centralized, pure classification
 * of "does this viewport need the ROTATE YOUR DEVICE / portrait-only
 * gameplay gate" - used by `GameScene`'s orientation gate (see
 * `src/scenes/GameScene.ts`) instead of scattering separate ad hoc
 * orientation/width/height checks through that file.
 *
 * The hard requirement this exists to satisfy: a desktop browser window
 * that happens to be narrower than it is tall (a developer's narrow split-
 * screen editor+browser layout, for example) must NEVER be treated as
 * "mobile landscape" and blocked - only an actual phone-class device. A
 * viewport-aspect-ratio check ALONE cannot tell those apart (both are
 * "wide and short" relative to the other axis); User-Agent sniffing alone
 * is exactly the "fragile UA-only logic" this module is asked to avoid
 * (trivially wrong for desktop browsers with a spoofed/overridden UA, new
 * device strings it doesn't recognize, etc). Instead this combines TWO
 * independent, robust signals:
 *
 *  1. Input capability - a coarse pointer (`matchMedia('(pointer: coarse)')`,
 *     the standard CSS Media Queries Level 4 signal for "the primary
 *     input mechanism has limited accuracy", true for touchscreens, false
 *     for mouse/trackpad) OR touch support (`hasTouch`, a fallback for
 *     browsers/environments where the `pointer` media feature isn't
 *     available). An ordinary desktop, even resized tall and narrow, has
 *     neither - this alone already rules out the "tall/narrow desktop
 *     window" case.
 *  2. Viewport size - even a touch-capable device can be a tablet, which
 *     this project deliberately does NOT lock to landscape (a tablet has
 *     enough room for a perfectly playable portrait layout - only PHONE-
 *     class screens are actually cramped). `PHONE_MAX_SHORT_SIDE_PX` is
 *     the shorter of width/height staying under a real-device threshold
 *     that covers every current phone (including phablets) while
 *     excluding the smallest common tablets (iPad mini's 768px shorter
 *     side and up).
 *
 * Both signals must agree for a viewport to be treated as "phone-like" -
 * this is deliberately conservative (a device that's ambiguous is treated
 * as NOT phone-like, i.e. never gated) since over-blocking a legitimate
 * desktop or tablet player is a much worse failure than under-blocking an
 * unusual phone configuration.
 *
 * Pure and framework-independent by design (no `window`/`matchMedia`
 * reads happen in this file) so it's directly unit-testable without a
 * browser - the caller reads the live signals once per resize/orientation
 * event and passes them in. Debouncing/hysteresis around rapid
 * resize/orientation-change events (so a player mid-rotation doesn't
 * flicker between classes) is the CALLER's responsibility (see
 * `GameScene`'s orientation-gate state machine) - this function is a
 * stateless, single-point-in-time classification.
 */

/** Shorter-side threshold, in CSS pixels, below which a touch-capable viewport is treated as phone-class rather than tablet-class. Comfortably above every current phone/phablet's shorter side (largest known: ~480px) and comfortably below the smallest common tablet's (iPad mini: 768px). */
export const PHONE_MAX_SHORT_SIDE_PX = 560;

export interface ViewportSignals {
  /** CSS pixels - `window.innerWidth`/`visualViewport.width` or equivalent. */
  width: number;
  /** CSS pixels - `window.innerHeight`/`visualViewport.height` or equivalent. */
  height: number;
  /** `window.matchMedia('(pointer: coarse)').matches` - the primary input mechanism has limited accuracy (a touchscreen), as opposed to a mouse/trackpad. */
  hasCoarsePointer: boolean;
  /** `navigator.maxTouchPoints > 0` (or `'ontouchstart' in window` as a fallback) - a secondary/fallback touch-capability signal, in case `matchMedia('(pointer: ...)')` itself is unavailable. */
  hasTouch: boolean;
}

export type GameplayViewportClass =
  /** Never orientation-gated, regardless of window aspect ratio. */
  | "desktop"
  /** Phone-class viewport, landscape - gameplay must be blocked/frozen behind the ROTATE YOUR DEVICE gate. */
  | "mobile-landscape"
  /** Phone-class viewport, portrait - gameplay allowed. */
  | "mobile-portrait";

/** Whether `signals` describes a phone-class device - touch/coarse-pointer capable AND small enough not to be a tablet. Exported separately from `classifyGameplayViewport()` since some callers only need this yes/no distinction (e.g. "should any orientation gate logic run at all for this device"). */
export function isPhoneLikeViewport(signals: ViewportSignals): boolean {
  const shorterSide = Math.min(signals.width, signals.height);
  return (signals.hasCoarsePointer || signals.hasTouch) && shorterSide <= PHONE_MAX_SHORT_SIDE_PX;
}

/**
 * The full classification `GameScene`'s orientation gate acts on. A
 * non-phone-like viewport (desktop OR tablet) is always `"desktop"` -
 * this project's portrait-only requirement is deliberately scoped to
 * phones alone (see this module's own doc). A phone-like viewport is
 * `"mobile-landscape"` when `width >= height` (a tie at an exact square
 * viewport is treated as landscape, i.e. blocked - erring toward
 * requiring an explicit rotate rather than silently allowing an
 * ambiguous orientation), otherwise `"mobile-portrait"`.
 */
export function classifyGameplayViewport(signals: ViewportSignals): GameplayViewportClass {
  if (!isPhoneLikeViewport(signals)) return "desktop";
  return signals.width >= signals.height ? "mobile-landscape" : "mobile-portrait";
}
